import React, { useState } from "react";
import { Link } from "react-router-dom";

const resolveProductImage = (product) => {
  const thumbnail = product?.thumbnail;
  if (typeof thumbnail === "string" && thumbnail) return thumbnail;
  if (thumbnail && typeof thumbnail === "object") {
    if (thumbnail.url) return thumbnail.url;
    if (thumbnail.secure_url) return thumbnail.secure_url;
  }

  const mediaImage = product?.media?.images?.[0];
  if (mediaImage?.secure_url) return mediaImage.secure_url;
  if (mediaImage?.url) return mediaImage.url;
  if (typeof product?.media?.[0] === "string") return product.media[0];
  if (product?.media?.[0]?.url) return product.media[0].url;

  return null;
};

const formatPriceParts = (price) => {
  if (price == null || price === "") {
    return { amount: "Price on request", unit: "" };
  }
  if (typeof price === "object") {
    const value = price.amount ?? price.value ?? price.per_day;
    const unit = price.unit || price.period || "day";
    if (value != null) return { amount: `₹${value}`, unit: `/${unit}` };
  }
  if (typeof price === "number") return { amount: `₹${price}`, unit: "/day" };
  return { amount: String(price), unit: "" };
};

const ProductCardImage = ({ product, className = "" }) => {
  const [failed, setFailed] = useState(false);
  const imageUrl = resolveProductImage(product);

  if (!imageUrl || failed) {
    return (
      <div className={`flex items-center justify-center bg-slate-100 text-xs text-slate-400 dark:bg-slate-700 ${className}`}>
        No image
      </div>
    );
  }

  return (
    <img
      src={imageUrl}
      alt={product.title || "Listing"}
      className={`object-cover ${className}`}
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
};

const ProductCard = ({ product, variant = "carousel" }) => {
  const { amount, unit } = formatPriceParts(product.price);
  const meta = [product.category, product.city].filter(Boolean).join(" · ");
  const isCarousel = variant === "carousel";

  if (isCarousel) {
    return (
      <Link
        to={product.url || `/products/product-details/${product.id}`}
        className="group flex w-[210px] flex-shrink-0 snap-start flex-col overflow-hidden rounded-xl border border-slate-200/80 bg-white shadow-sm transition-all hover:border-blue-300 hover:shadow-md dark:border-slate-600 dark:bg-slate-800 sm:w-[230px]"
      >
        <div className="relative h-[118px] w-full overflow-hidden bg-slate-100 dark:bg-slate-700">
          <ProductCardImage
            product={product}
            className="h-full w-full transition-transform duration-300 group-hover:scale-105"
          />
        </div>
        <div className="flex min-h-[96px] flex-col p-2.5">
          <p className="line-clamp-2 text-[13px] font-semibold leading-snug text-slate-900 group-hover:text-blue-600 dark:text-slate-100">
            {product.title || "Untitled listing"}
          </p>
          {meta && (
            <p className="mt-1 truncate text-[11px] text-slate-500 dark:text-slate-400">{meta}</p>
          )}
          <div className="mt-auto flex items-end justify-between gap-1 pt-2">
            <div className="min-w-0">
              <span className="block truncate text-sm font-bold tabular-nums text-blue-600 dark:text-blue-400">
                {amount}
              </span>
              {unit && (
                <span className="text-[10px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  {unit.replace("/", "")}
                </span>
              )}
            </div>
            {product.rating > 0 && (
              <span className="flex-shrink-0 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                ★ {Number(product.rating).toFixed(1)}
              </span>
            )}
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={product.url || `/products/product-details/${product.id}`}
      className="group flex gap-3 overflow-hidden rounded-xl border border-slate-200/80 bg-white p-2.5 shadow-sm transition-all hover:border-blue-300 hover:shadow-md dark:border-slate-600 dark:bg-slate-800"
    >
      <div className="relative h-[72px] w-[72px] flex-shrink-0 overflow-hidden rounded-lg bg-slate-100 dark:bg-slate-700">
        <ProductCardImage
          product={product}
          className="h-full w-full transition-transform group-hover:scale-105"
        />
      </div>
      <div className="min-w-0 flex-1">
        <p className="line-clamp-2 text-sm font-semibold leading-snug text-slate-900 group-hover:text-blue-600 dark:text-slate-100">
          {product.title || "Untitled listing"}
        </p>
        {meta && (
          <p className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400">{meta}</p>
        )}
        <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className="text-sm font-bold tabular-nums text-blue-600 dark:text-blue-400">{amount}</span>
          {unit && (
            <span className="text-[11px] text-slate-500 dark:text-slate-400">{unit}</span>
          )}
          {product.rating > 0 && (
            <span className="text-xs text-amber-600 dark:text-amber-400">
              ★ {Number(product.rating).toFixed(1)}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
};

const ChatProductCards = ({ products = [], compact = false }) => {
  if (!products?.length) return null;

  const useCarousel = !compact && products.length > 1;

  if (useCarousel) {
    return (
      <div className="mt-2 w-full">
        <div className="mb-2 flex items-center justify-between gap-2 px-0.5">
          <p className="text-[11px] font-medium text-slate-500 dark:text-slate-400">
            {products.length} listing{products.length !== 1 ? "s" : ""}
          </p>
          {products.length > 2 && (
            <p className="text-[10px] text-slate-400 dark:text-slate-500">Swipe →</p>
          )}
        </div>
        <div className="relative -mx-1">
          <div className="flex gap-2.5 overflow-x-auto overscroll-x-contain px-1 pb-1 snap-x snap-mandatory show-scrollbar">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} variant="carousel" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`mt-2 grid w-full gap-2.5 ${compact ? "grid-cols-1" : "grid-cols-1"}`}>
      {products.map((product) => (
        <ProductCard key={product.id} product={product} variant="list" />
      ))}
    </div>
  );
};

export default ChatProductCards;
